import React, { useEffect, useMemo } from 'react';
import { IconButton } from '@mui/material';
import { Tooltip } from 'react-tooltip';

import TabsButton from 'components/Buttons/TabsButton';
import { DocumentFiles } from 'common/document/interfaces/document-files';
import PositionedMenu from 'components/Menu/PositionedMenu';
import {
  reasonsForRejectionOfDocument,
  reasonsForRejectionOfDocumentMap,
} from 'common/document/ui/reasons-for-rejection-of-document';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { IOption } from 'common/ui/interfaces/selectors';
import { DocumentRejectionReasonEnum } from 'common/document/enums/document-rejection-reason.enum';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { AnimatedLoader } from 'components/Loaders/AnimatedLoader';
import { ReactComponent as EditIcon } from 'assets/images/edit-icon.svg';
import { ReactComponent as TrashIcon } from 'assets/images/trash-icon.svg';

import styles from './styles.module.scss';
import { ICanDownloadDriverLicenseResult } from '../DOTMedicalCardView/Utils/can-download-driver-license';
import PdfWithToken from '../../PdfWithToken';
import { TextTractImageView } from 'features/Dashboard/TextTractImageView';

type InputProps = {
  isLoading?: boolean;
  polygon?: any | null;
  documents: DocumentFiles[];
  currentDocument: DocumentFiles | undefined;
  setCurrentDocument: (document: DocumentFiles | undefined) => void;
  onReject?: (reason: IRejectReasonDocument) => void;
  onApprove?: () => void;
  canApprove?: {
    status: boolean;
    helpText: string;
  };
  canDownload?: ICanDownloadDriverLicenseResult;
  onDownload?: () => void;
  onPurge?: () => void;
  onEdit?: () => void;
};

export const DocumentImageViewer = ({
  isLoading = false,
  documents,
  currentDocument,
  setCurrentDocument,
  onReject,
  onApprove,
  canApprove = {
    status: true,
    helpText: '',
  },
  canDownload = {
    status: 'success',
    helpText: '',
  },
  onDownload,
  onPurge,
  onEdit,
}: InputProps) => {
  useEffect(() => {
    setCurrentDocument(documents.length ? documents[0] : undefined);
  }, [documents]);

  const handleReject = (reason: IOption) => {
    const rejectionDetails =
      reasonsForRejectionOfDocumentMap[
        reason.value as DocumentRejectionReasonEnum
      ];

    onReject &&
      onReject({
        rejectionReason: reason.value as DocumentRejectionReasonEnum,
        rejectionDetails,
      });
  };

  const isPdf = useMemo(() => {
    return currentDocument?.fileType === 'application/pdf';
  }, [currentDocument?.fileType]);

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <div className={styles.imageActions}>
          {documents.map(
            (doc: DocumentFiles) =>
              !!doc && (
                <TabsButton
                  key={doc.fileKey}
                  active={currentDocument?.fileKey === doc.fileKey}
                  warning={!doc.fileUrl}
                  onClick={() => {
                    setCurrentDocument(doc);
                  }}
                >
                  {doc.documentName}
                </TabsButton>
              )
          )}
        </div>
        <div className={styles.documentActions}>
          {onReject && (
            <PositionedMenu
              label="Reason for Rejection"
              items={reasonsForRejectionOfDocument}
              onSelected={handleReject}
            >
              Reject
            </PositionedMenu>
          )}
          {onApprove && (
            <>
              <div
                data-tooltip-id="approve-action-tooltip"
                data-tooltip-html={canApprove?.helpText}
                data-tooltip-place="bottom"
              >
                <ButtonWithArrowIcon
                  size="small"
                  onClick={onApprove}
                  disabled={!canApprove?.status}
                >
                  Approve
                </ButtonWithArrowIcon>
              </div>
              <Tooltip
                id="approve-action-tooltip"
                className={styles.tooltip}
                noArrow={true}
              />
            </>
          )}
          {onDownload && (
            <>
              <div
                data-tooltip-id="download-action-tooltip"
                data-tooltip-html={canDownload?.helpText}
                data-tooltip-place="bottom"
              >
                <ButtonWithArrowIcon
                  size="small"
                  onClick={onDownload}
                  downArrow={true}
                  disabled={canDownload?.status === 'disabled'}
                >
                  Download
                </ButtonWithArrowIcon>
              </div>
              <Tooltip
                id="download-action-tooltip"
                className={styles.tooltip}
                noArrow={true}
              />
            </>
          )}
        </div>
      </div>
      {!isLoading && currentDocument?.fileUrl ? (
        <>
          {isPdf ? (
            <PdfWithToken fileUrl={currentDocument.fileUrl} />
          ) : (
            <TextTractImageView polygon={null} src={currentDocument.fileUrl} />
          )}
        </>
      ) : (
        <div className={styles.empty}>
          {isLoading && (
            <div className={styles.loaderWrapper}>
              <AnimatedLoader isLoading={isLoading} />
            </div>
          )}
          {!isLoading &&
            currentDocument?.documentName.toLowerCase() === 'verification' &&
            'Enter License Number to Verify'}
        </div>
      )}
      <div className={styles.footerActions}>
        {onEdit && !isPdf && (
          <IconButton
            style={{
              background: '#fff',
              borderRadius: '50%',
              padding: '10px 10px 11px 13px',
            }}
            onClick={onEdit}
          >
            <EditIcon />
          </IconButton>
        )}
        {onPurge && (
          <IconButton
            style={{
              background: '#fff',
              borderRadius: '50%',
              padding: '10px 11px 11px 12px',
            }}
            onClick={onPurge}
          >
            <TrashIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
};
